import React, { useEffect } from 'react';

interface MetaProps {
  title: string;
  description?: string;
  canonical?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  ogImageAlt?: string;
  ogImageWidth?: number;
  ogImageHeight?: number;
  twitterCard?: 'summary' | 'summary_large_image';
  twitterSite?: string;
  twitterCreator?: string;
  robots?: string;
  ogType?: string;
  ogUrl?: string;
  // Open Graph Article extensions
  articlePublishedTime?: string; // ISO8601 preferred
  articleModifiedTime?: string; // ISO8601 preferred
  articleAuthor?: string;
  articleSection?: string;
  articleTags?: string[];
}

const Meta: React.FC<MetaProps> = ({ title, description, canonical, ogTitle, ogDescription, ogImage, ogImageAlt, ogImageWidth, ogImageHeight, twitterCard = 'summary_large_image', twitterSite, twitterCreator, robots, ogType = 'website', ogUrl, articlePublishedTime, articleModifiedTime, articleAuthor, articleSection, articleTags }) => {
  useEffect(() => {
    if (title) {
      document.title = title;
    }

    if (description) {
      let meta = document.querySelector('meta[name="description"]') as HTMLMetaElement | null;
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute('name', 'description');
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', description);
    }

    if (canonical) {
      let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
      if (!link) {
        link = document.createElement('link');
        link.setAttribute('rel', 'canonical');
        document.head.appendChild(link);
      }
      link.setAttribute('href', canonical);

      // Ensure hreflang alternates for Japanese site
      const ensureHrefLang = (lang: string, href: string) => {
        const selector = `link[rel='alternate'][hreflang='${lang}']`;
        let alt = document.querySelector(selector) as HTMLLinkElement | null;
        if (!alt) {
          alt = document.createElement('link');
          alt.setAttribute('rel', 'alternate');
          alt.setAttribute('hreflang', lang);
          document.head.appendChild(alt);
        }
        alt.setAttribute('href', href);
      };
      try {
        ensureHrefLang('ja-JP', canonical);
        ensureHrefLang('x-default', canonical);
      } catch (_) {
        // fail open
      }
    }

    if (robots) {
      let robotsMeta = document.querySelector('meta[name="robots"]') as HTMLMetaElement | null;
      if (!robotsMeta) {
        robotsMeta = document.createElement('meta');
        robotsMeta.setAttribute('name', 'robots');
        document.head.appendChild(robotsMeta);
      }
      robotsMeta.setAttribute('content', robots);
    }

    const setMeta = (attr: { name?: string; property?: string }, content?: string) => {
      if (!content) return;
      const selector = attr.name ? `meta[name='${attr.name}']` : `meta[property='${attr.property}']`;
      let el = document.querySelector(selector) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        if (attr.name) el.setAttribute('name', attr.name);
        if (attr.property) el.setAttribute('property', attr.property);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta({ property: 'og:title' }, ogTitle || title);
    setMeta({ property: 'og:description' }, ogDescription || description);
    if (ogImage) setMeta({ property: 'og:image' }, ogImage);
    if (ogImageWidth) setMeta({ property: 'og:image:width' }, String(ogImageWidth));
    if (ogImageHeight) setMeta({ property: 'og:image:height' }, String(ogImageHeight));
    // Provide consistent site_name and image alt for social preview accessibility
    setMeta({ property: 'og:site_name' }, 'うのつのぶこ');
    if (ogImage) setMeta({ property: 'og:image:alt' }, ogImageAlt || ogTitle || title || 'うのつのぶこ');
    setMeta({ property: 'og:type' }, ogType);
    setMeta({ property: 'og:url' }, ogUrl || canonical);
    setMeta({ name: 'twitter:card' }, twitterCard);
    setMeta({ name: 'twitter:title' }, ogTitle || title);
    setMeta({ name: 'twitter:description' }, ogDescription || description);
    if (ogImage) setMeta({ name: 'twitter:image' }, ogImage);
    if (ogImage && (ogImageAlt || ogTitle || title)) setMeta({ name: 'twitter:image:alt' }, ogImageAlt || ogTitle || title);
    if (twitterSite) setMeta({ name: 'twitter:site' }, twitterSite);
    if (twitterCreator) setMeta({ name: 'twitter:creator' }, twitterCreator);

    // Open Graph article extensions (only when relevant)
    if (ogType === 'article') {
      if (articlePublishedTime) setMeta({ property: 'article:published_time' }, articlePublishedTime);
      if (articleModifiedTime) setMeta({ property: 'article:modified_time' }, articleModifiedTime);
      if (articleAuthor) setMeta({ property: 'article:author' }, articleAuthor);
      if (articleSection) setMeta({ property: 'article:section' }, articleSection);
      // For article:tag we want multiple tags, so remove existing and append new ones
      const existingArticleTags = document.querySelectorAll("meta[property='article:tag']");
      existingArticleTags.forEach((node) => node.parentElement?.removeChild(node));
      if (Array.isArray(articleTags)) {
        articleTags.forEach((tag) => {
          if (!tag) return;
          const metaEl = document.createElement('meta');
          metaEl.setAttribute('property', 'article:tag');
          metaEl.setAttribute('content', tag);
          document.head.appendChild(metaEl);
        });
      }
    }
  }, [title, description, canonical, robots, ogType, ogUrl, ogTitle, ogDescription, ogImage, ogImageAlt, ogImageWidth, ogImageHeight, twitterCard, twitterSite, twitterCreator, articlePublishedTime, articleModifiedTime, articleAuthor, articleSection, articleTags]);

  return null;
};

export default Meta;


